import React, { memo } from 'react';
import { Button, Col, Row, Typography } from 'components';
import {
  DeleteIcon,
  EditIcon,
  OutlineArrowDownIcon,
  OutlineArrowUpIcon,
  WarehouseNavIcon,
} from 'components/common/icons';
import { SHIPPING_METHOD_MAPPING } from 'feature/clientTabs/shipping/shippingAddresses/shippingAddress';
import { SHIPPING_MEASUREMENT_RANGE_MAPPING, SHIPPING_RATE_METRICS_MAPPING } from 'staticData/form/client/shipping';
import styles from './../styles.module.scss';
import { HasAccess } from 'components/common/accessControl';
import { PERMISSIONS } from 'constants/permissions';
import { SpecialRate } from './../index';
import { useShippingRateCard } from 'feature/countryTabs/shipping/shippingRates/shippingRateCard/useShippingRateCard';

const { Text } = Typography;
export const SpecialShippingRateCard = memo<{
  specialRate: SpecialRate;
  onHandleEdit: (i: SpecialRate) => void;
  onHandleDelete: (i: SpecialRate) => void;
}>(({ specialRate, onHandleEdit, onHandleDelete }) => {
  const { isSeeAllVisible, isAllShown, onShowAllStateChange, priceList } = useShippingRateCard({
    prices: specialRate.prices || [],
  });
  return (
    <>
      <Col className={styles.special_rate_card} span={24}>
        <Row justify={'space-between'}>
          <Col>
            <Row align={'middle'} className={'gap-6'}>
              <Text className={'font-weight-600'}>WAREHOUSE {specialRate.warehouseName}</Text>
              <WarehouseNavIcon />
            </Row>
          </Col>
          <Col>
            <HasAccess role={PERMISSIONS.clients.special_shipping_rate.delete}>
              <Button type={'link'} icon={<DeleteIcon />} onClick={() => onHandleDelete(specialRate)} />
            </HasAccess>
            <HasAccess role={PERMISSIONS.clients.special_shipping_rate.write}>
              <Button type={'link'} icon={<EditIcon />} onClick={() => onHandleEdit(specialRate)} />
            </HasAccess>
          </Col>
          <Col span={24}>
            <Text className={styles.warehouse_country}>
              {specialRate.countryName}, {specialRate.city}
            </Text>
          </Col>
        </Row>
        <Row className={styles.shipping_details} justify={'space-between'}>
          <Col>
            {specialRate.shippingMethod && SHIPPING_METHOD_MAPPING[specialRate.shippingMethod] && (
              <Row className={styles.shipping_method_container} align={'middle'}>
                <Col className={styles.shipping_method}>{SHIPPING_METHOD_MAPPING[specialRate.shippingMethod].icon}</Col>
                <Col>
                  <Text>{SHIPPING_METHOD_MAPPING[specialRate.shippingMethod].text}</Text>
                </Col>
              </Row>
            )}
          </Col>
        </Row>
        {(priceList[0].metrics || priceList[0].measurementRange || priceList[0].price) && (
          <Row>
            <Col span={24}>
              <Text className={styles.price_title}>Price</Text>
            </Col>
            <Col span={24}>
              {priceList.map((price, index: number) => {
                return (
                  <Row className={styles.special_rate_price} key={index} justify={'space-between'}>
                    <Col>
                      <Text type={'secondary'}>
                        {SHIPPING_RATE_METRICS_MAPPING[price.metrics]}:{' '}
                        {SHIPPING_MEASUREMENT_RANGE_MAPPING[price.measurementRange]}
                      </Text>
                    </Col>
                    <Col>
                      {price.price && (
                        <Text type={'secondary'} className={'font-weight-500'}>
                          {price.price} €
                        </Text>
                      )}
                    </Col>
                  </Row>
                );
              })}
            </Col>
            {isSeeAllVisible && (
              <Col span={24}>
                <Row align={'middle'} justify={'end'}>
                  <Col>
                    <Button
                      type={'link'}
                      variant={'link'}
                      style={{
                        color: '#308D82',
                        textDecoration: 'underline',
                        padding: 0,
                        margin: 0,
                        fontSize: 14,
                        fontWeight: 400,
                      }}
                      onClick={onShowAllStateChange}
                    >
                      {isAllShown ? 'See Less' : 'See All'}
                      {isAllShown ? (
                        <OutlineArrowUpIcon size={'sm'} color={'#308D82'} />
                      ) : (
                        <OutlineArrowDownIcon size={'sm'} color={'#308D82'} />
                      )}
                    </Button>
                  </Col>
                </Row>
              </Col>
            )}
            {specialRate.specialHandling && (
              <>
                <Col span={24}>
                  <Text className={styles.special_handling_title}>Special Handling</Text>
                </Col>
                <Col span={24}>
                  <Text type={'secondary'}>{specialRate.specialHandling}</Text>
                </Col>
              </>
            )}
          </Row>
        )}
      </Col>
    </>
  );
});
